/**
 * Cookie Consent Context
 *
 * Provides consent state and management functions throughout the app.
 * Handles vendor initialization when consent is granted and cleanup when revoked.
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import type { ConsentState, ConsentCategories, EditableCategories } from '../lib/consent/types'
import {
  getConsent,
  setConsent,
  createAcceptAllConsent,
  createRejectAllConsent,
  createConsentState,
  CONSENT_VERSION,
} from '../lib/consent/storage'
import {
  initAnalyticsVendors,
  initMarketingVendors,
  initStrictlyNecessaryVendors,
  cleanupAnalyticsCookies,
  cleanupMarketingCookies,
} from '../lib/consent/vendors'
import { isNativeMobile } from '../mobile/capacitor'
import { updateGoogleConsent } from '../lib/consent/googleTag'

interface CookieConsentContextValue {
  /** Current consent state, null if not yet given */
  consent: ConsentState | null
  /** Whether Google Analytics is initialized and ready to receive events */
  analyticsReady: boolean
  /** Whether to show the consent banner */
  showBanner: boolean
  /** Whether the preferences modal is open */
  showModal: boolean
  /** Accept all cookies */
  acceptAll: () => void
  /** Reject all non-essential cookies */
  rejectAll: () => void
  /** Save custom preferences */
  savePreferences: (categories: EditableCategories) => void
  /** Open the preferences modal */
  openModal: () => void
  /** Close the preferences modal */
  closeModal: () => void
  /** Check if a specific category is consented */
  hasCategory: (category: keyof ConsentCategories) => boolean
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null)

interface CookieConsentProviderProps {
  children: ReactNode
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const nativeMobile = isNativeMobile()
  const [consent, setConsentState] = useState<ConsentState | null>(null)
  const [showBanner, setShowBanner] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [analyticsReady, setAnalyticsReady] = useState(false)

  // Initialize Sentry immediately (strictly necessary for stability/security)
  useEffect(() => {
    initStrictlyNecessaryVendors()
  }, [])

  // Read consent on mount
  useEffect(() => {
    const existingConsent = getConsent()
    setConsentState(existingConsent)

    // Cookie consent is a web surface. Native apps keep non-essential vendors
    // disabled unless an existing consent choice is available, without prompting.
    setShowBanner(!nativeMobile && existingConsent === null)
    setInitialized(true)
  }, [nativeMobile])

  // Initialize vendors when consent changes
  useEffect(() => {
    let cancelled = false

    updateGoogleConsent({
      analytics: consent?.categories.analytics === true,
      marketing: consent?.categories.marketing === true,
    })

    if (!initialized || !consent) {
      setAnalyticsReady(false)
      return () => {
        cancelled = true
      }
    }

    // Initialize vendors based on consent
    if (consent.categories.analytics) {
      void initAnalyticsVendors().then((ready) => {
        if (!cancelled) setAnalyticsReady(ready)
      })
    } else {
      setAnalyticsReady(false)
    }
    if (consent.categories.marketing) {
      void initMarketingVendors()
    }

    return () => {
      cancelled = true
    }
  }, [consent, initialized])

  const acceptAll = useCallback(() => {
    const newConsent = createAcceptAllConsent()
    setConsent(newConsent)
    updateGoogleConsent({ analytics: true, marketing: true })
    setConsentState(newConsent)
    setShowBanner(false)
    setShowModal(false)
  }, [])

  const rejectAll = useCallback(() => {
    const newConsent = createRejectAllConsent()
    setConsent(newConsent)
    updateGoogleConsent({ analytics: false, marketing: false })
    setConsentState(newConsent)
    setShowBanner(false)
    setShowModal(false)

    cleanupAnalyticsCookies()
    cleanupMarketingCookies()
  }, [])

  const savePreferences = useCallback((categories: EditableCategories) => {
    const fullCategories: ConsentCategories = {
      necessary: true,
      ...categories,
    }

    // Determine choice type
    let choice: 'all' | 'none' | 'custom' = 'custom'
    if (categories.functional && categories.analytics && categories.marketing) {
      choice = 'all'
    } else if (!categories.functional && !categories.analytics && !categories.marketing) {
      choice = 'none'
    }

    const newConsent = createConsentState(fullCategories, choice)
    setConsent(newConsent)
    updateGoogleConsent({
      analytics: categories.analytics,
      marketing: categories.marketing,
    })
    setConsentState(newConsent)
    setShowBanner(false)
    setShowModal(false)

    // Always clean up disabled categories, including stale cookies from older consent versions.
    if (!categories.analytics) {
      cleanupAnalyticsCookies()
    }
    if (!categories.marketing) {
      cleanupMarketingCookies()
    }
  }, [])

  const openModal = useCallback(() => {
    if (nativeMobile) return
    setShowModal(true)
  }, [nativeMobile])

  const closeModal = useCallback(() => {
    setShowModal(false)
  }, [])

  const hasCategory = useCallback(
    (category: keyof ConsentCategories): boolean => {
      if (category === 'necessary') return true
      if (!consent) return false
      return consent.categories[category] === true
    },
    [consent]
  )

  const value: CookieConsentContextValue = {
    consent,
    analyticsReady,
    showBanner,
    showModal,
    acceptAll,
    rejectAll,
    savePreferences,
    openModal,
    closeModal,
    hasCategory,
  }

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>
}

/**
 * Hook to access consent context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext)
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider')
  }
  return context
}

/**
 * Export consent version for reference
 */
// eslint-disable-next-line react-refresh/only-export-components
export { CONSENT_VERSION }
